import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useCallback, useEffect } from 'react';
import { Transition } from 'react-transition-group';
import { I as InlinePortalProvider } from '../Portal/InlinePortalProvider-DpZ1TsoC.js';
import { S as Shroud, O as OuterWrapper, W as Wrapper, H as Header, C as Content, F as Footer } from './Modal.styles-FMsTPcol.js';
import { g as getElementFromRef } from '../LocalModules/storm-ui-utils/dist/esm/getElementFromRef-DUWqD2Fg-DUWqD2Fg.js';
import { P as Portal } from '../Portal/Portal-CfuGnhf3.js';
import { T as TaktIdProvider, a as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { P as PreventScroll } from '../LocalModules/storm-ui-utils/dist/esm/PreventScroll-C7cxX_Px-zZoGpe3b.js';
import { H as Heading } from '../Heading/Heading-CFwpkzNx.js';
import { C as CloseButton } from '../Button/CloseButton/CloseButton-BhFVupYi.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';
import { t as trapTabFocus } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/trapTabFocus-vpg0lK2r-Bk1xQ7S4.js';

const fadeDuration = 50;
const Modal = ({
  children,
  closeButtonLabel = 'Close modal',
  closeButtonProps,
  contentClassName = '',
  footer,
  hasCloseButton = true,
  header,
  isOpen = false,
  modalElementId,
  onClose = noop,
  padding = 'small',
  taktId,
  taktValue,
  toggleEl,
  ...rest
}) => {
  const closeRef = useRef(null);
  const wrapperRef = useRef(null);
  const modalContainerRef = useRef(null);
  const teardownModal = useCallback(() => {
    // Bring focus back to the toggle element
    if (toggleEl) {
      const element = getElementFromRef(toggleEl);
      if (element !== null && element !== void 0 && element.focus) {
        element.focus();
      }
    }
  }, [toggleEl]);
  useEffect(() => () => {
    // Run `teardownModal()` if the Modal was open at unmount time.
    teardownModal === null || teardownModal === void 0 || teardownModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleKeyUp = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose === null || onClose === void 0 || onClose();
    }
  };
  const handleKeyDown = event => {
    if (modalContainerRef.current) {
      trapTabFocus(event, modalContainerRef.current);
    }
  };
  const handleClick = event => {
    const {
      target,
      currentTarget
    } = event;
    if (target === currentTarget) {
      event.preventDefault();
      onClose === null || onClose === void 0 || onClose();
    }
  };
  const handleEntered = () => {
    const focusableEls = getFocusableElements(wrapperRef.current);
    if (focusableEls.length > 0) {
      focusableEls[0].focus();
    }
  };
  return /*#__PURE__*/React__default.createElement(Transition, {
    timeout: fadeDuration,
    appear: true,
    in: isOpen,
    onExit: teardownModal,
    onEntered: handleEntered,
    nodeRef: modalContainerRef
  }, state => {
    if (state === 'exited') {
      return null;
    }
    return /*#__PURE__*/React__default.createElement(Portal, {
      containerId: modalElementId
    }, /*#__PURE__*/React__default.createElement(TaktIdProvider, {
      taktId: taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('modal')
    }, /*#__PURE__*/React__default.createElement(Shroud, _extends({}, rest, {
      onMouseDown: handleClick,
      onKeyUp: handleKeyUp,
      onKeyDown: handleKeyDown,
      role: "presentation",
      ref: modalContainerRef,
      $fadeDuration: fadeDuration,
      $transitionState: state
    }), /*#__PURE__*/React__default.createElement(PreventScroll, null, /*#__PURE__*/React__default.createElement(OuterWrapper, {
      $transitionState: state,
      ref: wrapperRef
    }, /*#__PURE__*/React__default.createElement(InlinePortalProvider, null, /*#__PURE__*/React__default.createElement(Wrapper, null, (header || hasCloseButton) && /*#__PURE__*/React__default.createElement(Header, null, /*#__PURE__*/React__default.createElement(Heading, {
      renderAs: "h1",
      size: "small"
    }, header), hasCloseButton && /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
      taktId: closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('close-button')
    }, ({
      getDataTaktAttributes
    }) => /*#__PURE__*/React__default.createElement(CloseButton, _extends({
      label: closeButtonLabel,
      onClick: onClose,
      ref: closeRef
    }, getDataTaktAttributes({
      taktValue: closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.taktValue
    }), closeButtonProps)))), /*#__PURE__*/React__default.createElement(Content, {
      className: contentClassName,
      $padding: padding
    }, children), footer && /*#__PURE__*/React__default.createElement(Footer, null, footer))))))));
  });
};

export { Modal as M };
//# sourceMappingURL=Modal-izJDPKqs.js.map
